import { GetMainMenuItemsParams } from 'ag-grid-community';

/**
 * Creates a filter function for ag-Grid main menu items based on audit view status.
 * When in audit view (auditor role), removes column-related menu items to prevent
 * users from modifying column configurations.
 *
 * @param isAuditView - Whether the current user is in audit view mode
 * @returns A function to be used with ag-Grid's `getMainMenuItems` prop
 *
 * @example
 * ```tsx
 * const isAuditView = PermissionService.get().isAuditor();
 * const getMainMenuItems = useCallback(
 *   createAuditViewMenuItems(isAuditView),
 *   [isAuditView]
 * );
 *
 * <AgTable
 *   getMainMenuItems={getMainMenuItems}
 *   // ... other props
 * />
 * ```
 */
export function createAuditViewMenuItems(isAuditView: boolean) {
  return (params: GetMainMenuItemsParams) => {
    if (!isAuditView) {
      return params.defaultItems;
    }

    // For audit view, filter out column-related menu items to prevent
    // auditors from modifying column configurations
    const itemsToExclude = new Set(['columnChooser', 'resetColumns', 'columnFilter']);

    return params.defaultItems?.filter((item: string) => !itemsToExclude.has(item)) ?? [];
  };
}
